import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { AddIcon, MinusIcon } from './Icons';
import { Module } from 'services/openapi';
import { AdminCheckBox } from 'app/components/AdminCheckbox';
import { CheckboxChangeEvent } from '@progress/kendo-react-inputs';

// Types remain the same
export interface PermissionItem {
    controlName: string;
    readWrite: boolean;
    read: boolean;
}

export interface PermissionCategory {
    id: string;
    title: string;
    isOpen?: boolean;
    items?: PermissionItem[];
}

// Styled Components (keeping the same styling)
const Container = styled.div`
  margin: 0 auto;
`;

const Header = styled.h2`
  font-size: 1.2rem;
  font-weight: 500;
  margin-bottom: 1rem;
`;

const CategoryRow = styled.div<{ isOpen: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 0.35rem;
  background-color: #fcfcfc;
  margin-bottom: ${props => (props.isOpen ? '0' : '10px')};
  a {
    color: ${props => (props.isOpen ? '#000000' : '#0075ff')};
    text-decoration: ${props => (props.isOpen ? 'none' : 'underline')};
    font-family: Inter;
    font-weight: 400;
    font-size: 15px;
    line-height: 100%;
    letter-spacing: 0%;
    text-decoration-style: solid;
    text-decoration-thickness: 0%;
    text-decoration-skip-ink: auto;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const CategoryTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CategoryContent = styled.div<{ isOpen: boolean }>`
  overflow: hidden;
  max-height: ${props => (props.isOpen ? '100%' : '0')};
  opacity: ${props => (props.isOpen ? '1' : '0')};
  transition: max-height 0.3s ease-in-out, opacity 0.3s ease-in-out;
  margin-bottom: ${props => (props.isOpen ? '1rem' : '0')};
  .checkbox-style {
    text-align: center;
  }
`;

const PermissionGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 200px 100px;
`;

const GridHeader = styled(PermissionGrid)`
  background-color: #e0e2e3;
  padding: 0.5rem 1rem 0.5rem 2rem;
  font-family: Inter;
  font-weight: 400;
  font-size: 12px;
  line-height: 100%;
  letter-spacing: 0%;
  &.TabNameStyle {
    padding: 0.5rem 1rem 0.5rem 1.5rem;
    display: flex;
    align-items: center;
    gap: 8px;
  }
  @media (min-width: 100px) and (max-width: 767px) {
    padding: 0.75rem 1rem 0.75rem 2rem;
  }
`;

const GridRow = styled(PermissionGrid) <{ isEven: boolean }>`
  background-color: ${props => (props.isEven ? '#ffffff' : '#F4F4F4')};
  padding: 0.5rem 1rem 0.5rem 2rem;
  align-items: center;
  font-family: Inter;
  font-weight: 400;
  font-size: 13px;
  line-height: 100%;
  letter-spacing: 0%;
  margin-bottom: 0px;
  @media (min-width: 100px) and (max-width: 767px) {
    padding: 0.75rem 1rem 0.75rem 2rem;
  }
`;

const MappedCategoryWrapper = styled.div`
  margin-bottom: 20px;
`;

const PopoverTrigger = styled.div`
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
`;

const PopoverContent = styled.div`
  position: absolute;
  width: max-content;
  background-color: white;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  padding: 8px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
  z-index: 10;
`;

const PopoverOption = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px;
  &:hover {
    background-color: #f5f5f5;
  }
  cursor: pointer;
`;

// Popover Component
const Popover = ({ children, content }) => {
    const [isOpen, setIsOpen] = useState(false);
    const popoverRef = React.useRef<any>(null);

    useEffect(() => {
        const handleClickOutside = event => {
            if (popoverRef.current && !popoverRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    return (
        <div style={{ position: 'relative' }} ref={popoverRef}>
            <PopoverTrigger onClick={() => setIsOpen(!isOpen)}>
                {children}
            </PopoverTrigger>
            {isOpen && <PopoverContent>{content}</PopoverContent>}
        </div>
    );
};

// Three Dots Icon
const ThreeDots = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
        <div
            style={{
                width: '4px',
                height: '4px',
                borderRadius: '50%',
                backgroundColor: '#333',
            }}
        ></div>
        <div
            style={{
                width: '4px',
                height: '4px',
                borderRadius: '50%',
                backgroundColor: '#333',
            }}
        ></div>
        <div
            style={{
                width: '4px',
                height: '4px',
                borderRadius: '50%',
                backgroundColor: '#333',
            }}
        ></div>
    </div>
);
type Props = {
    rolePermissionsList?: Module[];
    setRolePermissionsList: React.Dispatch<React.SetStateAction<Module[] | null>>;
    loading?: boolean;
};

const PermissionsAccordion = ({
    rolePermissionsList,
    setRolePermissionsList,
}: Props) => {
    const [expandedModules, setExpandedModules] = useState<{
        [moduleId: string]: boolean;
    }>({});

    // State for tracking checkbox values in popovers
    const [popoverStates, setPopoverStates] = useState({
        modules: {},
    });

    // Toggle module expansion
    const handleToggleCategory = (moduleId: string) => {
        setExpandedModules(prev => ({
            ...prev,
            [moduleId]: !prev[moduleId],
        }));
    };

    // Core function to handle selection change
    const handleSelectionChange = (controlId: string, isSelected: boolean) => {
        // One-way dependency map (Parent → Dependents)
        const controlDependencyMap: Record<string, string[]> = {
            //user data
            'eb60fcfa-3977-4557-8ace-f413f5ceaff2': [
                '0c1c0433-e551-4c0a-a6f4-20646640586a',
            ],
            '63bcb227-70ce-43c7-a2d2-c4937925f469': [
                '0c1c0433-e551-4c0a-a6f4-20646640586a',
            ],
            '1dbc0106-aee7-4280-8712-41b97a83f16e': [
                '0c1c0433-e551-4c0a-a6f4-20646640586a',
            ],
            'ba9f31ec-d5b3-4ee6-af05-03478035239d': [
                '0c1c0433-e551-4c0a-a6f4-20646640586a',
            ],
            'fa21443c-e52d-428e-b279-65e882da24ea': [
                '0c1c0433-e551-4c0a-a6f4-20646640586a',
            ],
            '91381163-dc7a-4f72-9ac5-fa0a55bb9767': [
                '0c1c0433-e551-4c0a-a6f4-20646640586a',
            ],

            //team data
            '22f8083b-f07f-455b-ba92-12fc218e4fdc': [
                'cb0bc69d-8be1-40ff-bb5a-75a73365304b',
            ],
            'f456cdba-050d-41eb-80b7-36e5d9d2d712': [
                'cb0bc69d-8be1-40ff-bb5a-75a73365304b',
            ],
            '3306e37f-f194-465e-840d-708ebe7e0321': [
                'cb0bc69d-8be1-40ff-bb5a-75a73365304b',
            ],
            '88919d8b-e0da-4070-b344-e172f870a024': [
                'cb0bc69d-8be1-40ff-bb5a-75a73365304b',
            ],
            '3e6417cd-6d5c-4154-b83d-ea21d2910298': [
                'cb0bc69d-8be1-40ff-bb5a-75a73365304b',
            ],
            '622c63c7-7960-4338-9bd1-326a4f910895': [
                'cb0bc69d-8be1-40ff-bb5a-75a73365304b',
            ],
            '5ad5034b-3391-4b67-ab65-2b1191668d9b': [
                'cb0bc69d-8be1-40ff-bb5a-75a73365304b',
            ],
            '8ea196fe-9e52-4704-ba27-0c004a872c4b': [
                'cb0bc69d-8be1-40ff-bb5a-75a73365304b',
            ],
            '64490bba-8f16-41c9-822d-3e2a315980a2': [
                'cb0bc69d-8be1-40ff-bb5a-75a73365304b',
            ],
            '8403c35b-c427-4729-9301-2e38dc55b315': [
                'cb0bc69d-8be1-40ff-bb5a-75a73365304b',
            ],

            //rider data
            'e88ff801-a055-4ef1-af58-92da7e82d370': [
                'f3a07b0d-a928-4a33-b72c-eca59d77e369',
            ],
            '12f8f904-a071-4082-9636-7730fb442fc6': [
                'f3a07b0d-a928-4a33-b72c-eca59d77e369',
            ],
            '6e77c7f4-e1b7-43d6-addc-90d9b940ae91': [
                'f3a07b0d-a928-4a33-b72c-eca59d77e369',
            ],
            '40021436-2f8d-4225-9c33-98f310c336fb': [
                'f3a07b0d-a928-4a33-b72c-eca59d77e369',
            ],
            'afc611a7-e5e3-495e-a151-5342dd330a86': [
                'f3a07b0d-a928-4a33-b72c-eca59d77e369',
            ],
            '7d82e977-4a6b-4f89-9685-992f27d93f76': [
                'f3a07b0d-a928-4a33-b72c-eca59d77e369',
            ],
            '78932e34-587e-4f9e-9673-37cf6ad8eca1': [
                'f3a07b0d-a928-4a33-b72c-eca59d77e369',
            ],

            //coach data
            'eb459f76-1d38-4f2f-b892-2380baad9285': [
                '9874720b-0550-42f9-9421-def0d1763c2b',
            ],
            'ae5f8925-1ef6-497e-893e-e5e16ae9a698': [
                '9874720b-0550-42f9-9421-def0d1763c2b',
            ],
            '59699754-919b-4ada-ac3a-4e2d52368883': [
                '9874720b-0550-42f9-9421-def0d1763c2b',
            ],
            '367cc285-2954-4857-b0d3-f5329c2ca062': [
                '9874720b-0550-42f9-9421-def0d1763c2b',
            ],
            '417438f9-7046-487f-bb2d-30ce0125f60d': [
                '9874720b-0550-42f9-9421-def0d1763c2b',
            ],

            //shows data
            '9c69a667-508f-4ab9-bcc3-860ffe622ba3': [
                '44f9c8c9-d1ef-44ce-b145-9c85b2452898',
            ],
            '466d4af0-b75f-45ea-aa32-578f3a9a0760': [
                '44f9c8c9-d1ef-44ce-b145-9c85b2452898',
            ],
            'ed83da42-5531-4bea-a17c-09cafe3fddf3': [
                '44f9c8c9-d1ef-44ce-b145-9c85b2452898',
            ],
            '380ecbf9-bae4-46f3-8955-02c8fb4eb893': [
                '44f9c8c9-d1ef-44ce-b145-9c85b2452898',
            ],
            'da0a37b4-8b8e-44b6-a117-041d7ea2c2fb': [
                '44f9c8c9-d1ef-44ce-b145-9c85b2452898',
            ],
            'e6d38155-5e38-47b7-af02-fd67fcacb200': [
                '44f9c8c9-d1ef-44ce-b145-9c85b2452898',
            ],
            '4bedf8b8-16e0-44a2-baa7-dfeab582619d': [
                '44f9c8c9-d1ef-44ce-b145-9c85b2452898',
            ],
            '0d06d459-26fc-42dc-9391-ff66c9ec164a': [
                '44f9c8c9-d1ef-44ce-b145-9c85b2452898',
            ],
            'e284636f-7274-4970-8c35-c51559c1fd3d': [
                '44f9c8c9-d1ef-44ce-b145-9c85b2452898',
            ],
            '513fe69b-479f-43a5-927a-c5ff78bb1fa2': [
                '44f9c8c9-d1ef-44ce-b145-9c85b2452898',
            ],
            '468f5851-41e5-4de4-9ce5-aaddb11ab7f9': [
                '44f9c8c9-d1ef-44ce-b145-9c85b2452898',
            ],
            '9bf7f89b-d901-47a7-a9ed-a855d85b6942': [
                '44f9c8c9-d1ef-44ce-b145-9c85b2452898',
            ],
            '4c4f4ca6-ac97-41f2-8d70-7064881e0629': [
                '44f9c8c9-d1ef-44ce-b145-9c85b2452898',
            ],
            '9465803b-dcb0-4660-a91f-8e869820ee03': [
                '44f9c8c9-d1ef-44ce-b145-9c85b2452898',
            ],
            '79d5740f-602f-41cb-b6c2-8208e42d9e22': [
                '44f9c8c9-d1ef-44ce-b145-9c85b2452898',
            ],

            //sponser data
            '3388a0d9-67f6-4a69-97c0-e7c70018e33f': [
                '5c3108d6-5ac2-4ba5-a1c0-437a9732bc7a',
            ],
            '5477a9d8-133c-4e39-9463-fc2fcf9151b0': [
                '5c3108d6-5ac2-4ba5-a1c0-437a9732bc7a',
            ],
        };

        // Reverse dependency map (Dependent → Parents)
        const reverseDependencyMap: Record<string, string[]> = {
            //user data
            '0c1c0433-e551-4c0a-a6f4-20646640586a': [
                'eb60fcfa-3977-4557-8ace-f413f5ceaff2',
                '63bcb227-70ce-43c7-a2d2-c4937925f469',
                '1dbc0106-aee7-4280-8712-41b97a83f16e',
                'ba9f31ec-d5b3-4ee6-af05-03478035239d',
                'fa21443c-e52d-428e-b279-65e882da24ea',
                '91381163-dc7a-4f72-9ac5-fa0a55bb9767',
            ],
            //team data
            'cb0bc69d-8be1-40ff-bb5a-75a73365304b': [
                '22f8083b-f07f-455b-ba92-12fc218e4fdc',
                'f456cdba-050d-41eb-80b7-36e5d9d2d712',
                '3306e37f-f194-465e-840d-708ebe7e0321',
                '88919d8b-e0da-4070-b344-e172f870a024',
                '3e6417cd-6d5c-4154-b83d-ea21d2910298',
                '622c63c7-7960-4338-9bd1-326a4f910895',
                '5ad5034b-3391-4b67-ab65-2b1191668d9b',
                '8ea196fe-9e52-4704-ba27-0c004a872c4b',
                '64490bba-8f16-41c9-822d-3e2a315980a2',
                '8403c35b-c427-4729-9301-2e38dc55b315',
            ],
            //rider data
            'f3a07b0d-a928-4a33-b72c-eca59d77e369': [
                'e88ff801-a055-4ef1-af58-92da7e82d370',
                '12f8f904-a071-4082-9636-7730fb442fc6',
                '6e77c7f4-e1b7-43d6-addc-90d9b940ae91',
                '40021436-2f8d-4225-9c33-98f310c336fb',
                'afc611a7-e5e3-495e-a151-5342dd330a86',
                '7d82e977-4a6b-4f89-9685-992f27d93f76',
                '78932e34-587e-4f9e-9673-37cf6ad8eca1',
            ],
            //coach data
            '9874720b-0550-42f9-9421-def0d1763c2b': [
                'eb459f76-1d38-4f2f-b892-2380baad9285',
                'ae5f8925-1ef6-497e-893e-e5e16ae9a698',
                '59699754-919b-4ada-ac3a-4e2d52368883',
                '367cc285-2954-4857-b0d3-f5329c2ca062',
                '417438f9-7046-487f-bb2d-30ce0125f60d',
            ],
            //shows data
            '44f9c8c9-d1ef-44ce-b145-9c85b2452898': [
                '9c69a667-508f-4ab9-bcc3-860ffe622ba3',
                '466d4af0-b75f-45ea-aa32-578f3a9a0760',
                'ed83da42-5531-4bea-a17c-09cafe3fddf3',
                '380ecbf9-bae4-46f3-8955-02c8fb4eb893',
                'da0a37b4-8b8e-44b6-a117-041d7ea2c2fb',
                'e6d38155-5e38-47b7-af02-fd67fcacb200',
                '4bedf8b8-16e0-44a2-baa7-dfeab582619d',
                '0d06d459-26fc-42dc-9391-ff66c9ec164a',
                'e284636f-7274-4970-8c35-c51559c1fd3d',
                '513fe69b-479f-43a5-927a-c5ff78bb1fa2',
                '468f5851-41e5-4de4-9ce5-aaddb11ab7f9',
                '9bf7f89b-d901-47a7-a9ed-a855d85b6942',
                '4c4f4ca6-ac97-41f2-8d70-7064881e0629',
                '9465803b-dcb0-4660-a91f-8e869820ee03',
                '79d5740f-602f-41cb-b6c2-8208e42d9e22',
            ],
            //sponser data
            '5c3108d6-5ac2-4ba5-a1c0-437a9732bc7a': [
                '3388a0d9-67f6-4a69-97c0-e7c70018e33f',
                '5477a9d8-133c-4e39-9463-fc2fcf9151b0',
            ],
        };

        setRolePermissionsList(prevRolePermissionList => {
            if (!prevRolePermissionList) return [];

            const updatedList = prevRolePermissionList.map(module => {
                if (!module.pages) return module;

                let moduleHasSelectedControl = false;

                const updatedPages = module.pages.map(page => {
                    if (!page.tabs) return page;

                    let pageHasSelectedControl = false;

                    const updatedTabs = page.tabs.map(tab => {
                        let tabHasSelectedControl = false;

                        let updatedButtonControls = tab.buttonControls
                            ? [...tab.buttonControls]
                            : [];
                        let updatedOtherControls = tab.otherControls
                            ? [...tab.otherControls]
                            : [];

                        // === BUTTON CONTROLS ===
                        if (tab.buttonControls) {
                            updatedButtonControls = updatedButtonControls.map(ctrl => {
                                let newIsSelected = ctrl.isSelected;

                                // Direct match
                                if (ctrl.id === controlId) {
                                    newIsSelected = isSelected;
                                }

                                // Apply forward dependency (parent → children)
                                const dependentIds = controlDependencyMap[controlId];
                                if (isSelected && ctrl.id && dependentIds?.includes(ctrl.id)) {
                                    newIsSelected = true;
                                }

                                // Apply reverse dependency (child → parents)
                                const reverseIds = reverseDependencyMap[controlId];
                                if (!isSelected && ctrl.id && reverseIds?.includes(ctrl.id)) {
                                    newIsSelected = false;
                                }

                                return {
                                    ...ctrl,
                                    isSelected: newIsSelected,
                                };
                            });

                            if (updatedButtonControls.some(c => c.isSelected)) {
                                tabHasSelectedControl = true;
                            }
                        }

                        // === OTHER CONTROLS ===
                        if (tab.otherControls) {
                            updatedOtherControls = updatedOtherControls.map(ctrl => {
                                let newIsSelected = ctrl.isSelected;

                                // Direct match
                                if (ctrl.id === controlId) {
                                    newIsSelected = isSelected;
                                }

                                // Forward dependency
                                const dependentIds = controlDependencyMap[controlId];
                                if (isSelected && ctrl.id && dependentIds?.includes(ctrl.id)) {
                                    newIsSelected = true;
                                }

                                // Reverse dependency
                                const reverseIds = reverseDependencyMap[controlId];
                                if (!isSelected && ctrl.id && reverseIds?.includes(ctrl.id)) {
                                    newIsSelected = false;
                                }

                                return {
                                    ...ctrl,
                                    isSelected: newIsSelected,
                                };
                            });

                            if (updatedOtherControls.some(c => c.isSelected)) {
                                tabHasSelectedControl = true;
                            }
                        }

                        pageHasSelectedControl =
                            pageHasSelectedControl || tabHasSelectedControl;

                        return {
                            ...tab,
                            isSelected: tabHasSelectedControl,
                            buttonControls: updatedButtonControls,
                            otherControls: updatedOtherControls,
                        };
                    });

                    moduleHasSelectedControl =
                        moduleHasSelectedControl || pageHasSelectedControl;

                    return {
                        ...page,
                        isSelected: pageHasSelectedControl,
                        tabs: updatedTabs,
                    };
                });

                return {
                    ...module,
                    isSelected: moduleHasSelectedControl,
                    pages: updatedPages,
                };
            });

            updateSelectAllState(updatedList);

            return updatedList;
        });
    };

    // Function to check if all controls in a module are selected
    const areAllControlsSelected = module => {
        if (!module.pages) return false;

        const allControlsSelected = module.pages.every(page => {
            if (!page.tabs) return true;

            return page.tabs.every(tab => {
                const allButtonControlsSelected =
                    tab.buttonControls?.every(control => control.isSelected) ?? true;

                const allOtherControlsSelected =
                    tab.otherControls?.every(control => control.isSelected) ?? true;

                return allButtonControlsSelected && allOtherControlsSelected;
            });
        });

        return allControlsSelected;
    };

    // Check if any controls in a module are selected
    const hasAnyControlSelected = module => {
        if (!module.pages) return false;

        return module.pages.some(page => {
            if (!page.tabs) return false;

            return page.tabs.some(tab => {
                const hasButtonControlSelected =
                    tab.buttonControls?.some(control => control.isSelected) ?? false;

                const hasOtherControlSelected =
                    tab.otherControls?.some(control => control.isSelected) ?? false;

                return hasButtonControlSelected || hasOtherControlSelected;
            });
        });
    };

    // Update Select All state based on individual checkboxes
    const updateSelectAllState = modulesList => {
        const updatedModuleStates = { ...popoverStates.modules };

        modulesList.forEach(module => {
            if (module.id) {
                const allSelected = areAllControlsSelected(module);
                updatedModuleStates[module.id] = {
                    ...updatedModuleStates[module.id],
                    selectAll: allSelected,
                };
            }
        });

        setPopoverStates(prev => ({
            ...prev,
            modules: updatedModuleStates,
        }));
    };

    // Initialize popover states when component mounts or rolePermissionsList changes
    useEffect(() => {
        if (rolePermissionsList) {
            updateSelectAllState(rolePermissionsList);
        }
    }, [rolePermissionsList]);

    // Toggle Select All for a module
    const toggleSelectAllModule = (moduleId: string) => {
        // Toggle the state
        setPopoverStates(prev => {
            const currentState = prev.modules[moduleId]?.selectAll || false;
            const newSelectAllState = !currentState;

            return {
                ...prev,
                modules: {
                    ...prev.modules,
                    [moduleId]: {
                        ...prev.modules[moduleId],
                        selectAll: newSelectAllState,
                    },
                },
            };
        });

        // Apply to the data
        setRolePermissionsList(prevRolePermissionList => {
            if (!prevRolePermissionList) return [];

            const isCurrentlySelected =
                popoverStates.modules[moduleId]?.selectAll || false;
            const newValue = !isCurrentlySelected;

            const updatedList = prevRolePermissionList.map(module => {
                if (module.id === moduleId) {
                    const updatedPages = module.pages?.map(page => {
                        const updatedTabs = page.tabs?.map(tab => {
                            const updatedButtonControls = tab.buttonControls?.map(
                                control => ({
                                    ...control,
                                    isSelected: newValue,
                                }),
                            );

                            const updatedOtherControls = tab.otherControls?.map(control => ({
                                ...control,
                                isSelected: newValue,
                            }));

                            return {
                                ...tab,
                                isSelected: newValue,
                                buttonControls: updatedButtonControls,
                                otherControls: updatedOtherControls,
                            };
                        });

                        return {
                            ...page,
                            isSelected: newValue,
                            tabs: updatedTabs,
                        };
                    });

                    return {
                        ...module,
                        isSelected: newValue,
                        pages: updatedPages,
                    };
                }
                return module;
            });

            return updatedList;
        });
    };
    return (
        <Container>
            <Header>Permissions</Header>

            {rolePermissionsList?.map(module => {
                const moduleSelectAllChecked =
                    popoverStates.modules[module?.id!]?.selectAll || false;

                return (
                    <div key={module.id}>
                        <CategoryRow isOpen={Boolean(expandedModules[module?.id!])}>
                            <CategoryTitle>
                                <Popover
                                    content={
                                        <div>
                                            <PopoverOption>
                                                <AdminCheckBox
                                                    label="Select All"
                                                    checked={moduleSelectAllChecked}
                                                    onChange={() => toggleSelectAllModule(module?.id!)}
                                                />
                                            </PopoverOption>
                                        </div>
                                    }
                                >
                                    <ThreeDots />
                                </Popover>
                                <a
                                    href="#"
                                    onClick={e => {
                                        e.preventDefault();
                                        handleToggleCategory(String(module.id));
                                    }}
                                >
                                    {module.moduleName}
                                </a>
                            </CategoryTitle>
                            {expandedModules[module?.id!] ? (
                                <MinusIcon
                                    onClick={() => handleToggleCategory(String(module.id))}
                                    size={20}
                                />
                            ) : (
                                <AddIcon
                                    onClick={() => handleToggleCategory(String(module.id))}
                                    size={20}
                                />
                            )}
                        </CategoryRow>

                        <CategoryContent isOpen={Boolean(expandedModules[module?.id!])}>
                            {module.pages?.map(page => (
                                <MappedCategoryWrapper key={page.id}>
                                    {page?.tabs?.map(tab => (
                                        <React.Fragment key={tab.id}>
                                            <GridHeader>
                                                <div>Control Name</div>
                                                <div className="checkbox-style">Give Permission</div>
                                            </GridHeader>

                                            {/* Render Button Controls */}
                                            {tab?.buttonControls?.map((control, index) => {
                                                return (
                                                    <GridRow
                                                        key={`button-${control.id}-${index}`}
                                                        isEven={index % 2 === 0}
                                                    >
                                                        <div>{control.controlName}</div>
                                                        <div className="checkbox-style">
                                                            <AdminCheckBox
                                                                checked={control.isSelected ?? false}
                                                                onChange={(e: CheckboxChangeEvent) =>
                                                                    handleSelectionChange(
                                                                        String(control.id),
                                                                        e.value,
                                                                    )
                                                                }
                                                            />
                                                        </div>
                                                    </GridRow>
                                                );
                                            })}

                                            {/* Render Other Controls */}
                                            {tab?.otherControls?.map((control, index) => {
                                                return (
                                                    <GridRow
                                                        key={`other-${control.id}-${index}`}
                                                        isEven={index % 2 === 0}
                                                    >
                                                        <div>{control.controlName}</div>
                                                        <div className="checkbox-style">
                                                            <AdminCheckBox
                                                                checked={control.isSelected ?? false}
                                                                onChange={e =>
                                                                    handleSelectionChange(
                                                                        String(control.id),
                                                                        e.value,
                                                                    )
                                                                }
                                                            />
                                                        </div>
                                                    </GridRow>
                                                );
                                            })}
                                        </React.Fragment>
                                    ))}
                                </MappedCategoryWrapper>
                            ))}
                        </CategoryContent>
                    </div>
                );
            })}
        </Container>
    );
};

export default PermissionsAccordion;
